<script>
export default {
	name: 'FireflyFooter',
	inheritAttrs: false,
};
</script>

<script setup>
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';
import AppFooterAccordion from '~/components/AppFooter/AppFooterAccordion.vue';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';

const props = defineProps({
	generalTab: { type: Object, default: () => ({}) },
	subsidiaryTab: { type: Object, default: () => ({}) },
	socialMediaTab: { type: Object, default: () => ({}) },
	tncTab: { type: Object, default: () => ({}) },
	copyrightTab: { type: Object, default: () => ({}) },
	
	componentIdAA: { type: String, default: '' },
});

const { isEditorMode } = useGlobalAEMState();


const isPropsDataEmpty = () => {
	const subsidiary = Object.keys(props.subsidiaryTab).length === 0;
	const socialMedia = Object.keys(props.socialMediaTab).length === 0;
	const tnc = Object.keys(props.tncTab).length === 0;
	const copyright = Object.keys(props.copyrightTab).length === 0;

	return subsidiary && socialMedia && tnc && copyright;
};


</script>



<template>
<template v-if="isEditorMode && (props.generalTab.isHideInDesktop || props.generalTab.isHideInMobile)">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: {{ [
			(props.generalTab.isHideInDesktop ? 'Desktop' : null),
			(props.generalTab.isHideInMobile ? 'Mobile' : null),
		].filter(Boolean).join(', ') }}</p>
	</div>
</template>

<template v-if="isPropsDataEmpty()">
	<p v-if="isEditorMode" class="text-center">🔧 Please click here to configure footer</p>
</template>
<footer
	v-else-if="(isMobileViewport && !props.generalTab.isHideInMobile) || (!isMobileViewport && !props.generalTab.isHideInDesktop)"
	class="FireflyFooter md:hidden"
	:class="{
		'>md:hidden': props.generalTab.isHideInDesktop,
	}"
>
	<div
		class="footer-inner-container bg-primary-firefly-orange-base"
		:class="{ '>md:rounded-t-7xl': props.generalTab.isCurvedCorners }"
	>
		<div class="generic-container flex flex-col gap-10 pb-11 pt-16 lg:pt-4 text-white">
			<div class="footer-top">
				<AppFooterAccordion
					componentNameAA="FireflyFooter"
					:items="props.generalTab.navigationMenu"
				/>
			</div>

			<div class="footer-middle flex">
				<div class="flex flex-col ml-auto lg:ml-0">
					<p class="font-bold mb-4">{{ props.socialMediaTab.label }}</p>
					<ul class="social-icons-list >lg:text-right >lg:flex >lg:items-center">
						<li
							v-for="socialItem in props.socialMediaTab.socialMediaList"
							:key="socialItem.iconImage"
							class="social-icons-list-item inline-block w-8 h-8"
						>
							<AppHyperlink
								v-aa="[
									{
										clickName: socialItem.iconAltText,
										clickComponentType: 'URL',
										componentName: 'FireflyFooter',
										componentID: props.componentIdAA,
									},
									{
										name: socialItem.iconAltText,
										type: isUrlMatchCurrentHostname(socialItem.iconUrl) ? 'other' : 'exit',
									},
								]"
								class="social-icons-anchor"
								:class="{
									'align-middle': socialItem.iconType !== 'icon-font-awesome-id',
								}"
								:href="socialItem.iconUrl"
								:target="socialItem.isOpenNewTab ? '_blank' : '_self'"
								rel="noopener noreferrer"
								:showExternalLinkIcon="false"
							>

								<FontAwesomeLazy
									v-if="socialItem.iconType === 'icon-font-awesome-id'"
									class="social-icon-logo fill-neutral-white-base hover:fill-primary-blue-extralight mx-auto"
									:iconId="socialItem.iconId"
									role="presentation"
								/>

								<img
									v-else
									:src="socialItem.iconImage"
									class="social-icons-image w-8 h-8 hover:opacity-50"
									loading="lazy"
									:alt="socialItem.iconAltText"
									role="presentation"
								/>
								<span class="sr-only">{{ socialItem.iconAltText }}</span>
							</AppHyperlink>
						</li>
					</ul>
				</div>
			</div>
			
			<div class="footer-bottom flex items-end lg:items-start lg:flex-col">
				<div class="partner-logo-wrapper mb-3 lg:mb-6">
					<AppHyperlink
						v-for="item in props.subsidiaryTab.subsidiaryList"
						:key="item.imageAltText"
						v-aa="[
							{
								clickName: item.imageAltText,
								clickComponentType: 'URL',
								componentName: 'FireflyFooter',
								componentID: props.componentIdAA,
							},
							{
								name: item.imageAltText,
								type: isUrlMatchCurrentHostname(item.imageUrl) ? 'other' : 'exit',
							},
						]"
						:href="item.imageUrl"
						:target="item.isOpenNewTab ? '_blank' : '_self'"
						class="partner-logo inline-block"
						:class="{ 'hidden': (isMobileViewport && item.isHideInMobile) || (!isMobileViewport && item.isHideInDesktop) }"
						:showExternalLinkIcon="false"
					>
						<img
							:src="item.image"
							class="h-[40px] max-w-none w-auto inline-block"
							loading="lazy"
							:alt="item.imageAltText"
						/>
					</AppHyperlink>
				</div>
				<div class="copyright-wrapper ml-auto lg:ml-0">
					<div class="flex flex-col items-end lg:items-start">
						<ul class="copyright-list mb-4 lg:mb-0 lg:flex lg:flex-col">
							<li
								v-for="(item, index) in props.tncTab.tncList"
								:key="`${item.title}-${index}`"
								class="copyright-item inline-block"
							>
								<AppHyperlink
									v-aa="[
										{
											clickName: item.title,
											clickComponentType: 'URL',
											componentName: 'FireflyFooter',
											componentID: props.componentIdAA,
										},
										{
											name: item.title,
											type: isUrlMatchCurrentHostname(item.titleUrl) ? 'other' : 'exit',
										},
									]"
									class="copyright-link text-neutral-white-base hover:text-primary-blue-extralight"
									:href="item.titleUrl"
									:target="item.isOpenNewTab ? '_blank' : '_self'"
									rel="noopener noreferrer"
									:showExternalLinkIcon="false"
								>
									<span class="copyright-link-text">{{ item.title }}</span>
								</AppHyperlink>
							</li>
						</ul>
						<p
							v-html-sanitize="props.copyrightTab.text"
							class="copyright-text whitespace-pre-line font-light text-xs lg:mt-6"
						></p>
					</div>
				</div>
			</div>
		</div>
	</div>
</footer>
</template>


<style lang="scss">
.global-footer {
	margin-top: auto;
}

html.showing-subpages {
	.FireflyFooter {
		display: none;
	}
}
</style>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.FireflyFooter {
	:deep(.footer-top-link) {
		&:hover .footer-top-link-text {
			color: white;
		}
	}
}

.social-icons-list {
	display: flex;
	@apply gap-8;

	@media #{var.$query-max-md} {
		@apply gap-x-10 gap-y-6;
	}
}

.partner-logo-wrapper {
	display: flex;
	flex-wrap: wrap;
	@apply gap-x-6;

	.AppHyperlink {
		
	}
}


.copyright-item {
	@apply font-semibold;
	
	&:not(:last-child) {
		@apply mr-5;

		@media #{var.$query-max-lg} {
			@apply mr-0 mb-4;
		}
	}
}

.copyright-text {
	@apply break-words text-right;

	html[dir="rtl"] & {
		@apply text-left;
	}
}

</style>
